import { isStandardPalletType } from '../constants/pallets';
import { WEIGHT_UNITS } from '@constants';
import {
  DIMENSION_UNITS,
  SKU_TEMPERATURE_UNITS,
  SKU_UNITS,
  WEIGHT_UNITS as SKU_FORM_WEIGHT_UNITS,
} from '@forms/SKUForm/constants';
import { Pallet, SelectedSKU, SKUFormValues } from '@forms/SKUForm/types';

/**
 * Recursively strips { label, value } wrapper objects down to just the value.
 * Used to prepare form data for API submission.
 */
export const stripLabelValue = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(stripLabelValue);
  }
  if (obj && typeof obj === 'object') {
    return Object.entries(obj).reduce((acc: any, [key, value]) => {
      if (
        value &&
        typeof value === 'object' &&
        'label' in value &&
        'value' in value
      ) {
        return { ...acc, [key]: value.value };
      }
      return { ...acc, [key]: stripLabelValue(value) };
    }, {});
  }
  return obj;
};

export const getOptionalNumberString = (value?: number): string => {
  return value === 0 || value === undefined || value === null
    ? ''
    : Number(value).toString();
};

/**
 * Build the API payload from cleaned (stripLabelValue'd) form data.
 * Used by both CreateSKU and UpdateSKU.
 */
export function buildSkuApiPayload(
  data: SKUFormValues,
  cleanedData: any,
  extraSkuFields?: Record<string, any>
) {
  const metaData =
    data.customFields?.map((field) => ({
      name: field.label,
      value: field.value,
      type: field.type,
    })) || [];

  const suppliers = (data.suppliers || []).map((s) => ({
    supplierId: s.value,
    name: s.label,
  }));

  const palletising =
    cleanedData.palletising?.map((pallet: Pallet) => ({
      ...pallet,
      palletId: undefined,
      id: pallet.palletId ?? '',
      ...(!isStandardPalletType(pallet.type)
        ? {
            dimensions: {
              length: pallet.length,
              width: pallet.width,
              height: pallet.height,
              dimensionsUnitOfMeasure: pallet.dimensionsUnitOfMeasure,
            },
          }
        : {}),
    })) ?? [];

  const temperatureControlRequirements =
    cleanedData.temperatureControlRequirements
      ? {
          temperatureControlType:
            cleanedData.temperatureControlRequirements.temperatureControlType,
          minimumTemperature: cleanedData.temperatureControlRequirements
            ?.minimumTemperature?.quantity
            ? {
                quantity:
                  cleanedData.temperatureControlRequirements.minimumTemperature
                    .quantity,
                unitOfMeasure:
                  cleanedData.temperatureControlRequirements.unitOfMeasure,
              }
            : null,
          maximumTemperature: cleanedData.temperatureControlRequirements
            ?.maximumTemperature?.quantity
            ? {
                quantity:
                  cleanedData.temperatureControlRequirements.maximumTemperature
                    .quantity,
                unitOfMeasure:
                  cleanedData.temperatureControlRequirements.unitOfMeasure,
              }
            : null,
        }
      : null;

  const productDetails = cleanedData.productId
    ? {
        productId: cleanedData.productId,
        name: cleanedData.productName,
        description: cleanedData.productDescription,
        brand: cleanedData.productBrand,
        category: cleanedData.productCategory,
        subCategory: cleanedData.productSubCategory,
        netVolume: cleanedData.productNetVolume,
        netVolumeUnitOfMeasure: cleanedData.productNetVolumeUnitOfMeasure,
        barcode: cleanedData.productBarcode,
      }
    : null;

  return {
    sku: {
      ...cleanedData,
      ...extraSkuFields,
      // Top-level mandatory field (lifted out of productDetails). Coerce the form string to a number.
      amountOfProductInSku: Number(cleanedData.amountOfProductInSku) || 1,
      lowStockThreshold:
        cleanedData.lowStockThreshold !== undefined &&
        cleanedData.lowStockThreshold !== null &&
        cleanedData.lowStockThreshold !== ''
          ? Number(cleanedData.lowStockThreshold)
          : null,
      metaData,
      palletising,
      temperatureControlRequirements,
      productDetails,
      suppliers,
    },
    skuImages: data.skuImage?.name
      ? [
          {
            id: crypto.randomUUID(),
            title: data.skuImage?.name,
            image: data.skuImage,
          },
        ]
      : [],
    barcodeImage: data.skuBarcodeImage?.name
      ? {
          id: crypto.randomUUID(),
          title: data.skuBarcodeImage?.name,
          image: data.skuBarcodeImage,
        }
      : {},
    deletedSkuImageIds: data.deletedSKUImageId ? [data.deletedSKUImageId] : [],
    deletedSkuBarcodeImageIds: data.deletedBarcodeImageId
      ? [data.deletedBarcodeImageId]
      : [],
    productBarcodeImage: data.skuProductBarcodeImage?.name
      ? {
          id: crypto.randomUUID(),
          title: data.skuProductBarcodeImage?.name,
          image: data.skuProductBarcodeImage,
        }
      : {},
    deletedProductBarcodeImageIds: data.deletedProductBarcodeImageId
      ? [data.deletedProductBarcodeImageId]
      : [],
    productImage: data.skuProductImage?.name
      ? {
          id: crypto.randomUUID(),
          title: data.skuProductImage?.name,
          image: data.skuProductImage,
        }
      : {},
    deletedProductImageIds: data.deletedProductImageId
      ? [data.deletedProductImageId]
      : [],
  };
}

/**
 * Map a SelectedSKU (from the API) to SKUFormValues.
 * When isClone=true, clears identifiers and images for a new SKU.
 */
export function mapSkuToFormValues(
  selected: SelectedSKU,
  isClone = false
): SKUFormValues {
  const { sku } = selected;

  const mapTemperatureControlRequirements = () => {
    if (!sku.temperatureControlRequirements) return null;
    return {
      temperatureControlType: sku.temperatureControlRequirements
        .temperatureControlType
        ? {
            label: sku.temperatureControlRequirements.temperatureControlType,
            value: sku.temperatureControlRequirements.temperatureControlType,
          }
        : null,
      minimumTemperature: sku.temperatureControlRequirements?.minimumTemperature
        ?.quantity
        ? {
            quantity:
              sku.temperatureControlRequirements.minimumTemperature.quantity,
          }
        : null,
      maximumTemperature: sku.temperatureControlRequirements.maximumTemperature
        ? {
            quantity:
              sku.temperatureControlRequirements.maximumTemperature.quantity,
          }
        : null,
      unitOfMeasure: {
        label:
          sku.temperatureControlRequirements.minimumTemperature
            ?.unitOfMeasure ??
          sku.temperatureControlRequirements.maximumTemperature
            ?.unitOfMeasure ??
          SKU_TEMPERATURE_UNITS[0].label,
        value:
          sku.temperatureControlRequirements.minimumTemperature
            ?.unitOfMeasure ??
          sku.temperatureControlRequirements.maximumTemperature
            ?.unitOfMeasure ??
          SKU_TEMPERATURE_UNITS[0].value,
      },
    };
  };

  const mapPalletising = () =>
    sku.palletising.map((pallet) => ({
      palletId: isClone ? '' : pallet.id,
      skuId: isClone ? '' : sku.skuId,
      type: pallet.type,
      customPalletSpecification: pallet.customPalletSpecification ?? undefined,
      amountOfSkusInLayer:
        pallet.amountOfSkusInLayer !== null &&
        pallet.amountOfSkusInLayer !== undefined
          ? pallet.amountOfSkusInLayer.toString()
          : '',
      amountOfLayersInPallet:
        pallet.amountOfLayersInPallet !== null &&
        pallet.amountOfLayersInPallet !== undefined
          ? pallet.amountOfLayersInPallet.toString()
          : '',
      customPalletWeightUnit: {
        label: pallet.customPalletWeightUnit ?? WEIGHT_UNITS[2].label,
        value: pallet.customPalletWeightUnit ?? WEIGHT_UNITS[2].value,
      },
      customPalletWeight: getOptionalNumberString(pallet.customPalletWeight),
      width:
        pallet.dimensions.width !== null &&
        pallet.dimensions.width !== undefined
          ? pallet.dimensions.width.toString()
          : undefined,
      length:
        pallet.dimensions.length !== null &&
        pallet.dimensions.length !== undefined
          ? pallet.dimensions.length.toString()
          : undefined,
      height:
        pallet.dimensions.height !== null &&
        pallet.dimensions.height !== undefined
          ? pallet.dimensions.height.toString()
          : undefined,
      dimensionsUnitOfMeasure: {
        label: pallet.dimensions.dimensionsUnitOfMeasure ?? '',
        value: pallet.dimensions.dimensionsUnitOfMeasure ?? '',
      },
    }));

  return {
    productId: sku.productDetails?.productId ?? '',
    productName: sku.productDetails?.name ?? '',
    productDescription: sku.productDetails?.description ?? '',
    productBrand: sku.productDetails?.brand ?? '',
    productCategory: sku.productDetails?.category ?? '',
    productSubCategory: sku.productDetails?.subCategory ?? '',
    productNetVolume: sku.productDetails?.netVolume?.toString() ?? '',
    productNetVolumeUnitOfMeasure: {
      label: sku.productDetails?.netVolumeUnitOfMeasure ?? '',
      value: sku.productDetails?.netVolumeUnitOfMeasure ?? '',
    },
    productBarcode: isClone ? '' : sku.productDetails?.barcode?.barcode ?? '',
    costPrices: sku.costPrices.map((cp) => ({
      currency: { label: cp.currency ?? '', value: cp.currency ?? '' },
      amount:
        cp.amount !== null && cp.amount !== undefined
          ? cp.amount.toString()
          : '',
      comment: cp.comment,
    })),
    customFields:
      sku.metaData?.map((cf) => ({
        id: cf.name ?? '',
        type: cf.type ?? '',
        label: cf.name ?? '',
        value: cf.value ?? '',
      })) ?? [],
    inventorySkuId: isClone ? '' : sku.inventorySkuId,
    skuId: isClone ? `${sku.skuId}-COPY` : sku.skuId,
    name: isClone ? `${sku.name} (Copy)` : sku.name ?? '',
    barcode: isClone ? '' : sku.barcode?.barcode ?? '',
    unitOfMeasure: {
      label: sku.unitOfMeasure ?? '',
      value: sku.unitOfMeasure ?? '',
    },
    amountOfProductInSku: (
      sku.amountOfProductInSku ??
      sku.productDetails?.amountOfProductInSku ??
      1
    ).toString(),
    lowStockThreshold:
      sku.lowStockThreshold !== null && sku.lowStockThreshold !== undefined
        ? sku.lowStockThreshold.toString()
        : '',
    description: sku.description ?? '',
    temperatureControlRequirements: mapTemperatureControlRequirements(),
    dimensions: {
      dimensionsUnitOfMeasure: {
        label: sku.dimensions.dimensionsUnitOfMeasure ?? '',
        value: sku.dimensions.dimensionsUnitOfMeasure ?? '',
      },
      length:
        sku.dimensions.length !== null && sku.dimensions.length !== undefined
          ? sku.dimensions.length.toString()
          : '',
      width:
        sku.dimensions.width !== null && sku.dimensions.width !== undefined
          ? sku.dimensions.width.toString()
          : '',
      height:
        sku.dimensions.height !== null && sku.dimensions.height !== undefined
          ? sku.dimensions.height.toString()
          : '',
    },
    grossWeightUnitOfMeasure: {
      label: sku.grossWeightUnitOfMeasure ?? '',
      value: sku.grossWeightUnitOfMeasure ?? '',
    },
    grossWeight:
      sku.grossWeight !== null && sku.grossWeight !== undefined
        ? sku.grossWeight.toString()
        : '',
    listPrices: sku.listPrices.map((lp) => ({
      currency: { label: lp.currency ?? '', value: lp.currency ?? '' },
      amount:
        lp.amount !== null && lp.amount !== undefined
          ? lp.amount.toString()
          : '',
      comment: lp.comment,
    })),
    image: isClone ? null : sku.images?.[0] ?? null,
    barcodeImage: isClone ? null : sku.barcodeImage ?? null,
    skuImage: null,
    skuBarcodeImage: null,
    deletedSKUImageId: '',
    deletedBarcodeImageId: '',
    productBarcodeImage: isClone
      ? null
      : sku.productDetails?.barcodeImage ?? null,
    skuProductBarcodeImage: null,
    deletedProductBarcodeImageId: '',
    productImage: isClone ? null : sku.productDetails?.image ?? null,
    skuProductImage: null,
    deletedProductImageId: '',
    palletising: mapPalletising(),
    suppliers: (sku.suppliers || []).map((s) => ({
      label: s.name,
      value: s.supplierId,
    })),
  };
}

export function mapSkuToProductSeedValues(
  selected: SelectedSKU
): SKUFormValues {
  const source = mapSkuToFormValues(selected);
  return {
    ...source,
    inventorySkuId: '',
    skuId: source.productId ? `${source.productId}-` : '',
    name: '',
    barcode: '',
    unitOfMeasure: SKU_UNITS[1],
    amountOfProductInSku: '',
    lowStockThreshold: '',
    description: '',
    temperatureControlRequirements: null,
    dimensions: {
      dimensionsUnitOfMeasure: DIMENSION_UNITS[1],
      length: '',
      width: '',
      height: '',
    },
    grossWeightUnitOfMeasure: SKU_FORM_WEIGHT_UNITS[1],
    grossWeight: '',
    costPrices: [],
    listPrices: [],
    customFields: [],
    image: null,
    barcodeImage: null,
    deletedSKUImageId: '',
    deletedBarcodeImageId: '',
    palletising: [],
    suppliers: [],
  };
}

export const skuUnitNoun = (
  sku: { unitOfMeasure?: string | null },
  otherFallback = 'SKU'
): string =>
  sku.unitOfMeasure && sku.unitOfMeasure.toLowerCase() !== 'other'
    ? sku.unitOfMeasure
    : otherFallback;

export const skuProductsPerUnit = (sku: {
  amountOfProductInSku?: number | null;
}): number => Number(sku.amountOfProductInSku) || 1;

export const skuPackDescription = (sku: {
  unitOfMeasure?: string | null;
  amountOfProductInSku?: number | null;
}): string => {
  const productsPerUnit = skuProductsPerUnit(sku);
  if (productsPerUnit > 1)
    return `${skuUnitNoun(sku)} of ${productsPerUnit.toLocaleString()}`;
  if (sku.unitOfMeasure?.toLowerCase() === 'item') return 'Single item';
  return skuUnitNoun(sku);
};

const comparableText = (text: string): string =>
  text.toLowerCase().replace(/,/g, '').trim();

export const skuPackLabel = (
  sku: {
    unitOfMeasure?: string | null;
    amountOfProductInSku?: number | null;
  },
  name: string | null | undefined
): string | null => {
  const description = skuPackDescription(sku);
  const isTheWholeName =
    comparableText(name ?? '') === comparableText(description);
  return isTheWholeName ? null : description;
};
